/**
 * Input sanitization utilities for XSS prevention
 */
export class Sanitizer {
  /**
   * Dangerous URL protocols that should be blocked
   */
  private static readonly DANGEROUS_PROTOCOLS = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
  ];

  /**
   * HTML entities map for escaping
   */
  private static readonly HTML_ENTITIES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '/': '&#x2F;',
  };

  /**
   * Sanitize URL to prevent XSS via javascript: protocol
   * @param url - URL to sanitize
   * @returns Sanitized URL
   * @throws Error if URL contains dangerous protocol
   */
  static sanitizeUrl(url: string): string {
    const trimmed = url.trim();

    // Check for dangerous protocols
    const lowerUrl = trimmed.toLowerCase();
    for (const protocol of this.DANGEROUS_PROTOCOLS) {
      if (lowerUrl.startsWith(protocol)) {
        throw new Error(`[CaredoveWidget] Blocked dangerous URL protocol: ${protocol}`);
      }
    }

    // Optionally upgrade HTTP to HTTPS (except localhost)
    // Note: We don't force this as some customers may need HTTP URLs
    // Remove this block entirely if it causes issues
    if (trimmed.startsWith('http://')) {
      // Allow localhost and local IPs (for development)
      if (!trimmed.match(/^http:\/\/(localhost|127\.0\.0\.1|192\.168\.|10\.)/)) {
        console.warn('[CaredoveWidget] HTTP URL detected, consider using HTTPS:', trimmed);
      }
    }

    return trimmed;
  }

  /**
   * Validate and sanitize a URL, returning null if invalid
   * @param url - URL to validate
   * @returns Sanitized URL or null if invalid
   */
  static validateUrl(url: string): string | null {
    try {
      const sanitized = this.sanitizeUrl(url);

      // Try to parse as URL to ensure it's valid
      new URL(sanitized);

      return sanitized;
    } catch {
      return null;
    }
  }

  /**
   * Sanitize content ID to prevent injection
   * Removes HTML tags and dangerous characters
   * @param contentId - Content ID to sanitize
   * @returns Sanitized content ID
   */
  static sanitizeContentId(contentId: string): string {
    // Remove any HTML tags
    let sanitized = contentId.replace(/<[^>]*>/g, '');

    // Remove script tags specifically (defense in depth)
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    return sanitized.trim();
  }

  /**
   * Escape HTML string for safe insertion into DOM
   * @param text - Text to escape
   * @returns HTML-escaped text
   */
  static escapeHtml(text: string): string {
    return text.replace(/[&<>"'\/]/g, char => this.HTML_ENTITIES[char] || char);
  }

  /**
   * Sanitize HTML content by escaping dangerous elements
   * For use when you need to display user-provided content
   * @param html - HTML string to sanitize
   * @returns Sanitized HTML
   */
  static sanitizeHtml(html: string): string {
    const div = document.createElement('div');
    div.textContent = html; // textContent automatically escapes
    return div.innerHTML;
  }

  /**
   * Strip all HTML tags from a string
   * @param html - HTML string
   * @returns Plain text
   */
  static stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Sanitize CSS value to prevent CSS injection
   * @param cssValue - CSS value to sanitize
   * @returns Sanitized CSS value
   */
  static sanitizeCss(cssValue: string): string {
    // Remove dangerous CSS patterns
    let sanitized = cssValue.replace(/expression\s*\(/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/behavior:/gi, '');
    sanitized = sanitized.replace(/-moz-binding:/gi, '');
    sanitized = sanitized.replace(/import/gi, '');

    return sanitized.trim();
  }

  /**
   * Validate hex color code
   * @param color - Color string to validate
   * @returns true if valid hex color
   */
  static isValidHexColor(color: string): boolean {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  }

  /**
   * Sanitize numeric value within a range
   * @param value - Value to sanitize
   * @param min - Minimum allowed value
   * @param max - Maximum allowed value
   * @param defaultValue - Default value if invalid
   * @returns Sanitized number
   */
  static sanitizeNumber(
    value: string | number,
    min: number,
    max: number,
    defaultValue: number
  ): number {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(num) || num < min || num > max) {
      return defaultValue;
    }

    return num;
  }
}
